/*
 * "Deliver to" address selector (snippets/delivery-address-selector.liquid).
 * There are two copies on the page (desktop top bar, mobile strip); a change
 * in one is mirrored in the other through the 'deliver-to:change' event.
 *
 * - Desktop: dropdown panel. Mobile: bottom sheet with overlay, scroll lock
 *   and swipe-down to close.
 * - Closes on outside click, Esc, or the overlay.
 * - Logged in: arrow keys move between addresses, Enter / Space picks one.
 *   Picking makes it the default through the app proxy (data-endpoint), so
 *   checkout uses it. The label changes at once and goes back if saving fails.
 * - Logged out: a 6-digit pincode is kept in localStorage (this browser only).
 */
(function () {
  var PINCODE_PATTERN = /^[1-9]\d{5}$/;
  var STORAGE_KEY = 'deliver-to-pincode';
  var SAVE_ERROR = "Couldn't update address, please try again.";
  var SWIPE_CLOSE = 80; // px dragged down before the sheet closes

  if (!window.customElements || customElements.get('deliver-to')) return;

  function readPincode() {
    try {
      var value = window.localStorage.getItem(STORAGE_KEY) || '';
      return PINCODE_PATTERN.test(value) ? value : '';
    } catch (error) {
      return '';
    }
  }

  function writePincode(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      // Private mode or blocked storage: the label still changes on this page.
    }
  }

  customElements.define(
    'deliver-to',
    class DeliverTo extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-trigger]');
        this.label = this.querySelector('[data-label]');
        this.panel = this.querySelector('[data-panel]');
        this.overlay = this.querySelector('[data-overlay]');
        this.list = this.querySelector('[data-list]');
        this.errorEl = this.querySelector('[data-error]');
        this.pinForm = this.querySelector('[data-pin-form]');
        this.isMobile = this.dataset.layout === 'mobile';
        this.busy = false;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.trigger.addEventListener('click', this.toggle.bind(this));
        if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this, true));
        if (this.list) {
          this.list.addEventListener('click', this.onListClick.bind(this));
          this.list.addEventListener('keydown', this.onListKeydown.bind(this));
        }
        if (this.pinForm) {
          this.pinForm.addEventListener('submit', this.onPinSubmit.bind(this));
          this.pinForm.elements.pincode.addEventListener('input', function (event) {
            event.target.value = event.target.value.replace(/\D/g, '').slice(0, 6);
          });
        }
        if (this.isMobile) this.setUpSwipe();

        document.addEventListener('deliver-to:change', this.onChange.bind(this));

        if (this.hasAttribute('data-guest')) {
          var pincode = readPincode();
          if (pincode) this.showPincode(pincode);
        }
      }

      // --- Open / close ---

      get isOpen() {
        return !this.panel.hidden;
      }

      toggle() {
        if (this.isOpen) this.close(true);
        else this.open();
      }

      open() {
        this.panel.hidden = false;
        if (this.overlay) this.overlay.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        if (this.isMobile) document.documentElement.classList.add('deliver-to-locked');
        // Next frame, so the bottom sheet and overlay animate in.
        requestAnimationFrame(
          function () {
            this.classList.add('is-open');
          }.bind(this)
        );

        var focusTarget =
          (this.list && this.list.querySelector('[tabindex="0"]')) ||
          (this.pinForm && this.pinForm.elements.pincode) ||
          this.panel.querySelector('.deliver-to__title');
        if (focusTarget) focusTarget.focus({ preventScroll: true });

        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('keydown', this.onKeydown);
      }

      close(returnFocus) {
        if (!this.isOpen) return;
        this.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('deliver-to-locked');
        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('keydown', this.onKeydown);
        this.panel.style.transform = '';

        var hide = function () {
          if (this.classList.contains('is-open')) return; // reopened meanwhile
          this.panel.hidden = true;
          if (this.overlay) this.overlay.hidden = true;
        }.bind(this);
        if (this.isMobile && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          setTimeout(hide, 250); // after the slide-down
        } else {
          hide();
        }

        if (returnFocus === true) this.trigger.focus({ preventScroll: true });
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close(false);
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
          return;
        }
        // The bottom sheet is modal: keep Tab inside it.
        if (event.key !== 'Tab' || !this.isMobile) return;
        var focusable = Array.prototype.filter.call(
          this.panel.querySelectorAll('a[href], button, input, [tabindex="0"]'),
          function (el) {
            return !el.disabled && el.offsetParent !== null;
          }
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      // Drag the sheet down from its top (or from anywhere while the list is
      // scrolled to the top) to close it.
      setUpSwipe() {
        var startY = 0;
        var distance = 0;
        var dragging = false;

        this.panel.addEventListener(
          'touchstart',
          function (event) {
            var inList = this.list && this.list.contains(event.target);
            if (inList && this.list.scrollTop > 0) return;
            if (event.target.closest('input, button, a')) return;
            dragging = true;
            distance = 0;
            startY = event.touches[0].clientY;
          }.bind(this),
          { passive: true }
        );

        this.panel.addEventListener(
          'touchmove',
          function (event) {
            if (!dragging) return;
            distance = Math.max(0, event.touches[0].clientY - startY);
            if (distance > 0) {
              event.preventDefault(); // stop the list / page scrolling instead
              this.panel.classList.add('is-dragging');
              this.panel.style.transform = 'translateY(' + distance + 'px)';
            }
          }.bind(this),
          { passive: false }
        );

        var end = function () {
          if (!dragging) return;
          dragging = false;
          this.panel.classList.remove('is-dragging');
          if (distance > SWIPE_CLOSE) {
            this.close(false);
          } else {
            this.panel.style.transform = '';
          }
        }.bind(this);
        this.panel.addEventListener('touchend', end);
        this.panel.addEventListener('touchcancel', end);
      }

      // --- Logged in: address list ---

      get cards() {
        return Array.prototype.slice.call(this.list.querySelectorAll('[data-address]'));
      }

      onListClick(event) {
        var card = event.target.closest('[data-address]');
        if (card) this.select(card);
      }

      onListKeydown(event) {
        var card = event.target.closest('[data-address]');
        if (!card) return;
        var cards = this.cards;
        var index = cards.indexOf(card);
        var next = null;

        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            next = cards[(index + 1) % cards.length];
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            next = cards[(index - 1 + cards.length) % cards.length];
            break;
          case 'Home':
            next = cards[0];
            break;
          case 'End':
            next = cards[cards.length - 1];
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.select(card);
            return;
          default:
            return;
        }
        event.preventDefault();
        cards.forEach(function (item) {
          item.tabIndex = item === next ? 0 : -1;
        });
        next.focus();
      }

      selectedCard() {
        return this.list.querySelector('[aria-selected="true"]');
      }

      // Marks the card with this id as chosen (radio, "Default" tag, label).
      applyAddress(id) {
        if (!this.list) return;
        var chosen = null;
        this.cards.forEach(function (card) {
          var match = card.dataset.address === id;
          if (match) chosen = card;
          card.setAttribute('aria-selected', String(match));
          card.tabIndex = match ? 0 : -1;
          var tag = card.querySelector('[data-default-tag]');
          if (tag) tag.hidden = !match;
        });
        if (chosen) {
          this.setLabel('Deliver to ', chosen.dataset.firstName + ' – ' + chosen.dataset.city + ' ' + chosen.dataset.zip);
        }
      }

      select(card) {
        if (this.busy) return;
        var previous = this.selectedCard();
        if (card === previous) {
          this.close(true);
          return;
        }

        this.busy = true;
        this.showError('');
        var previousId = previous ? previous.dataset.address : '';
        this.broadcast({ addressId: card.dataset.address });
        card.classList.add('is-loading');
        card.setAttribute('aria-busy', 'true');

        var data = card.dataset;
        fetch(this.dataset.endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            action: 'update',
            id: data.address,
            address: {
              name: data.name,
              address1: data.address1,
              address2: data.address2,
              city: data.city,
              provinceCode: data.province,
              zip: data.zip,
              phone: data.phone,
            },
            tag: data.tag,
            makeDefault: true,
          }),
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Save failed: ' + response.status);
          })
          .then(
            function () {
              this.close(true);
            }.bind(this),
            function () {
              if (previousId) this.broadcast({ addressId: previousId });
              this.showError(SAVE_ERROR);
            }.bind(this)
          )
          .then(
            function () {
              this.busy = false;
              card.classList.remove('is-loading');
              card.removeAttribute('aria-busy');
            }.bind(this)
          );
      }

      // --- Logged out: pincode ---

      onPinSubmit(event) {
        event.preventDefault();
        var input = this.pinForm.elements.pincode;
        var value = input.value.trim();
        if (!PINCODE_PATTERN.test(value)) {
          input.setAttribute('aria-invalid', 'true');
          this.showError('Enter a valid 6-digit pincode.');
          input.focus();
          return;
        }
        input.removeAttribute('aria-invalid');
        this.showError('');
        writePincode(value);
        this.broadcast({ pincode: value });
        this.close(true);
      }

      showPincode(value) {
        this.setLabel('Deliver to ', value);
        if (this.pinForm) this.pinForm.elements.pincode.value = value;
      }

      // --- Shared ---

      broadcast(detail) {
        document.dispatchEvent(new CustomEvent('deliver-to:change', { detail: detail }));
      }

      onChange(event) {
        var detail = event.detail || {};
        if (detail.addressId) this.applyAddress(detail.addressId);
        if (detail.pincode) this.showPincode(detail.pincode);
      }

      setLabel(prefix, value) {
        var strong = document.createElement('span');
        strong.className = 'deliver-to__value';
        strong.textContent = value;
        this.label.textContent = prefix;
        this.label.appendChild(strong);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = !message;
      }
    }
  );
})();
